import React, { useState, useMemo } from "react";
import { Icon } from "@/components/Icon";
import PlayerPropRow, { groupPlayerProps, surname } from "./PlayerPropRow";
import { formatOdds, useOddsFormat } from "@/lib/odds";

/* SGPBuilder (design SBX-05, §4) — build a same-game parlay from this event only.
   Rules mirror the slip + server: exactly one game line (radio) + any number of props,
   one side per prop. Draft is local until "Add to Bet Slip", which toggles each leg into
   the real slip (the game line replaces the event's existing game line). */

const signed = (n) => { const v = Number(n); return Number.isFinite(v) ? (v > 0 ? `+${v}` : `${v}`) : ""; };

function gameLineOptions(event, groups) {
  const shortName = (side) => String((side === "away" ? event.away_team : event.home_team) || "").split(" ").slice(-1)[0];
  const opts = [];
  (groups.spread || []).forEach((o) => opts.push({ o, label: `${shortName(o.side)} ${signed(o.line)}` }));
  (groups.total || []).forEach((o) => opts.push({ o, label: `${o.side === "over" ? "Over" : "Under"} ${o.line}` }));
  (groups.moneyline || []).forEach((o) => opts.push({ o, label: `${shortName(o.side)} ML` }));
  return opts;
}

export default function SGPBuilder({ event, groups, players, choose, has, eventLabel }) {
  const fmt = useOddsFormat();
  const [chip, setChip] = useState("popular");
  const [line, setLine] = useState(null);          // selected game-line outcome
  const [props, setProps] = useState({});          // propKey -> outcome (+player/prop)
  const [added, setAdded] = useState(false);

  const lineOpts = useMemo(() => gameLineOptions(event, groups), [event, groups]);
  const propTypes = useMemo(() => {
    const c = {}; players.forEach((p) => groupPlayerProps(p.outcomes).forEach((g) => { c[g.prop || "Other"] = (c[g.prop || "Other"] || 0) + 1; }));
    return Object.keys(c);
  }, [players]);

  const selectProp = (pk, player, prop) => (o) => { setAdded(false); setProps((s) => { const n = { ...s }; if (String(n[pk]?.id) === String(o.id)) delete n[pk]; else n[pk] = { ...o, player, prop }; return n; }); };
  const propPicked = (pk) => (o) => String(props[pk]?.id) === String(o.id);

  const legs = [...(line ? [{ ...line, _kind: "line" }] : []), ...Object.values(props).map((o) => ({ ...o, _kind: "prop" }))];
  const combined = legs.reduce((a, l) => a * (Number(l.odds) || 1), 1);
  const n = legs.length;

  const marketTag = (l) => l._kind === "line" ? (l.market === "moneyline" ? "MONEYLINE" : l.market === "spread" ? "SPREAD" : "TOTAL") : "PLAYER PROP";
  const legText = (l) => l._kind === "line"
    ? (l.market === "total" ? `${l.side === "over" ? "Over" : "Under"} ${l.line}` : l.market === "moneyline" ? `${surname((l.side === "away" ? event.away_team : event.home_team))} ML` : `${surname((l.side === "away" ? event.away_team : event.home_team))} ${signed(l.line)}`)
    : (l.side === "yes" || l.side === "no")
      ? `${surname(l.player)} ${l.prop || ""} ${l.side === "no" ? "No" : "Yes"}`.trim()
      : `${surname(l.player)} ${l.side === "under" ? "Under" : "Over"} ${l.line} ${l.prop || ""}`.trim();

  const addToSlip = () => { legs.forEach((l) => { if (!has(String(l.id))) choose(l); }); setAdded(true); };

  const showLines = chip === "popular" || chip === "lines";
  const visiblePlayers = players.map((p) => ({ p, rows: groupPlayerProps(p.outcomes).filter((g) => chip === "popular" ? true : chip === "lines" ? false : g.prop === chip) }))
    .map((x) => chip === "popular" ? { ...x, rows: x.rows.slice(0, 1) } : x)
    .filter((x) => x.rows.length);

  const Chip = ({ id, label }) => (
    <button type="button" onClick={() => setChip(id)} className="flex h-9 shrink-0 items-center rounded-[11px] px-3.5 transition-colors"
      style={chip === id ? { background: "#FAFAFA", color: "#0A0A0A", font: "700 13px Outfit" } : { background: "#141417", boxShadow: "inset 0 0 0 1px #26262B", color: "#a1a1aa", font: "600 13px Outfit" }}>{label}</button>
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]" data-testid="sgp-builder">
      <div className="min-w-0 space-y-4 pb-24 lg:pb-0">
        {/* chips */}
        <div className="no-scrollbar -mr-4 flex gap-2 overflow-x-auto pb-1 sm:mr-0">
          <Chip id="popular" label="Popular" />
          <Chip id="lines" label="Game lines" />
          {propTypes.map((t) => <Chip key={t} id={t} label={t} />)}
        </div>

        {/* game line — pick one */}
        {showLines && lineOpts.length > 0 && (
          <div className="overflow-hidden rounded-[18px] p-4 sm:px-5" style={{ background: "#111114", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.05), 0 10px 26px rgba(0,0,0,.22)" }}>
            <div className="mb-3 flex items-baseline gap-2">
              <h3 className="font-display text-[15px] font-extrabold tracking-tight">Game line</h3>
              <span className="text-xs text-[#71717a]">Pick one</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {lineOpts.map(({ o, label }) => {
                const sel = String(line?.id) === String(o.id);
                return (
                  <button key={o.id} type="button" data-testid={`sgp-line-${o.id}`} onClick={() => { setAdded(false); setLine(sel ? null : o); }}
                    className="flex h-12 w-full items-center gap-3 rounded-xl px-3.5 transition-colors"
                    style={sel ? { background: "rgba(0,214,50,.1)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.45)" } : { background: "#141417", boxShadow: "inset 0 0 0 1px #26262B" }}>
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ boxShadow: `inset 0 0 0 2px ${sel ? "#00D632" : "#3F3F46"}` }}>{sel && <span className="h-2.5 w-2.5 rounded-full bg-[#00D632]" />}</span>
                    <span className="min-w-0 flex-1 truncate text-left" style={{ font: "600 15px Outfit", color: "#fff" }}>{label}</span>
                    <span className="shrink-0 font-display font-extrabold tabular-nums" style={{ color: sel ? "#00D632" : "#fff" }}>{formatOdds(o.odds, fmt)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* player props — any number, one side each */}
        {visiblePlayers.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-baseline gap-2 px-1">
              <h3 className="font-display text-[15px] font-extrabold tracking-tight">Player props</h3>
              <span className="text-xs text-[#71717a]">Add as many as you like · one side each</span>
            </div>
            {visiblePlayers.map(({ p, rows }) => (
              <div key={p.player} className="overflow-hidden rounded-[18px]" style={{ background: "#111114", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.05), 0 10px 26px rgba(0,0,0,.22)" }}>
                <div className="flex items-center px-4 sm:px-5" style={{ height: 48, borderBottom: "1px solid #1A1A1E" }}>
                  <span className="truncate font-display text-[15px] font-extrabold">{p.player}</span>
                </div>
                {rows.map((g, i) => {
                  const pk = `${p.player}|${g.prop}|${g.line}`;
                  return (
                    <div key={i}>
                      <div className="hidden lg:block"><PlayerPropRow hideName player={p.player} propLabel={g.prop} line={g.line} over={g.over} under={g.under} yesno={g.yesno} onPick={selectProp(pk, p.player, g.prop)} picked={propPicked(pk)} last={i === rows.length - 1} /></div>
                      <div className="lg:hidden"><PlayerPropRow hideName mobile player={p.player} propLabel={g.prop} line={g.line} over={g.over} under={g.under} yesno={g.yesno} onPick={selectProp(pk, p.player, g.prop)} picked={propPicked(pk)} last={i === rows.length - 1} /></div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* summary — full sticky card on desktop (SBX-05); compact bottom bar on mobile (SBX-M6) */}
      <div className="hidden lg:sticky lg:top-24 lg:block lg:self-start">
        <div className="overflow-hidden rounded-[18px] p-4 sm:px-5" style={{ background: "#111114", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.05), 0 10px 26px rgba(0,0,0,.22)" }} data-testid="sgp-summary">
          <p className="font-display text-[11px] font-extrabold uppercase tracking-[.24em] text-[#00D632]">Same Game Parlay</p>
          <p className="mt-0.5 truncate text-sm text-[#a1a1aa]">{eventLabel}</p>
          {n === 0 ? (
            <p className="mt-4 text-sm text-[#71717a]">Pick a game line and add props to build your same-game parlay.</p>
          ) : (
            <>
              <div className="relative mt-4 space-y-2.5">
                {legs.map((l, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#00D632]"><Icon name="check" size={10} className="text-[#0A0A0A]" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] font-extrabold uppercase tracking-[.14em] text-[#71717a]">{marketTag(l)}</p>
                      <p className="truncate text-[13px] font-semibold text-white">{legText(l)}</p>
                    </div>
                    <span className="shrink-0 font-display text-xs font-extrabold tabular-nums text-[#a1a1aa]">{formatOdds(l.odds, fmt)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-end justify-between border-t border-[#1A1A1E] pt-3">
                <div>
                  <p className="text-xs text-[#71717a]">{n} selection{n > 1 ? "s" : ""}</p>
                  <p className="text-xs text-[#71717a]">$10 pays ${(10 * combined).toFixed(2)}</p>
                </div>
                <span className="font-display font-extrabold tabular-nums text-[#00D632]" style={{ fontSize: 30, letterSpacing: "-.02em" }}>{formatOdds(combined, fmt)}</span>
              </div>
              <button type="button" onClick={addToSlip} disabled={n < 2 || added} data-testid="sgp-add"
                className="mt-3 w-full rounded-[15px] py-3.5 font-display text-[15px] font-extrabold transition-colors"
                style={(n < 2 || added) ? { background: "#141417", color: "#52525b" } : { background: "#00D632", color: "#0A0A0A" }}>
                {added ? "On your Bet Slip" : n < 2 ? "Add 2+ to build a parlay" : `Add ${n} to Bet Slip`}
              </button>
              <p className="mt-2.5 text-[11px] leading-relaxed text-[#71717a]">Price shown is the legs multiplied. The final price is confirmed when you place the bet.</p>
            </>
          )}
        </div>
      </div>

      {/* mobile summary bar (SBX-M6) — fixed above the TabBar; z-50 covers any stale slip bar */}
      {n > 0 && (
        <div className="fixed left-1/2 z-50 flex h-[60px] w-[calc(100%-24px)] max-w-[520px] -translate-x-1/2 items-center gap-3 rounded-[18px] px-3 lg:hidden"
          style={{ bottom: "calc(76px + 8px + env(safe-area-inset-bottom))", background: "#18181C", boxShadow: "inset 0 0 0 1px #2F2F35, 0 16px 40px rgba(0,0,0,.6)" }} data-testid="sgp-bar">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00D632] font-display text-xs font-extrabold text-[#0A0A0A]">{n}</span>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-sm font-bold text-white">{n} selection{n > 1 ? "s" : ""}</span>
            <span className="block text-xs font-bold text-[#00D632]">{formatOdds(combined, fmt)}</span>
          </span>
          <button type="button" onClick={addToSlip} disabled={n < 2 || added}
            className="flex h-[40px] shrink-0 items-center rounded-xl px-4 font-bold"
            style={(n < 2 || added) ? { background: "#141417", color: "#52525b" } : { background: "#00D632", color: "#0A0A0A" }}>
            {added ? "On your Bet Slip" : n < 2 ? "Add 2+" : `Add ${n}`}
          </button>
        </div>
      )}
    </div>
  );
}
